import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category", label: "短剧分类", end: false },
  { to: "/news", label: "资讯", end: false },
  { to: "/about", label: "关于", end: false },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setMenuOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="ai短剧 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50 md:h-10 md:w-10">
            <i className="ri-clapperboard-fill text-lg md:text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950 md:text-xl">
              ai短剧
            </span>
            <span className="hidden text-[10px] font-medium tracking-[0.22em] text-foreground-400 md:block">
              AI SHORT DRAMA
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submit} className="hidden items-center md:flex" role="search">
            <div className="flex h-10 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                name="q"
                placeholder="搜索短剧 / 资讯"
                aria-label="全站搜索"
                className="h-full w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-52"
              />
            </div>
          </form>

          <Link
            to="/category"
            className="hidden shrink-0 items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:flex"
          >
            <i className="ri-compass-3-line text-base"></i>
            <span className="whitespace-nowrap">逛分类</span>
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <form onSubmit={submit} className="mb-4" role="search">
            <div className="flex h-11 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                name="q"
                placeholder="输入关键词搜索短剧或资讯"
                aria-label="全站搜索"
                className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <nav className="flex flex-col" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-lg text-foreground-400"></i>
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}